import {
    type FilterConfigModalItem,
    isFilterConfigModalItemColor,
    isFilterConfigModalItemDefault,
    isFilterConfigModalItemMultiple,
    isFilterConfigModalItemPrice,
    isFilterConfigModalItemSearchable,
    isFilterConfigModalItemSimpleRange,
} from '@rocco/ui/filter-sort/types/filter-config-modal';
import {
    type FilterState,
    isLocationFilterState,
    isMultipleValuesFilterState,
    isRangeFilterState,
    isSearchableListFilterState,
    isSingleValueFilterState,
} from '@rocco/ui/filter-sort/view-models/filter-state/filter-state';
import type { FilterStatesKey } from '@rocco/ui/filter-sort/view-models/filter-states';
import type { AggregationArgs } from '@rocco/ui/filter-sort/hooks/use-search-filters';
import { UIConfigurationError } from '@rocco/exceptions/ui-configuration-error';
import {
    FiltersModalColors,
    FiltersModalMultipleValues,
    FiltersModalMultipleValuesButtons,
    FiltersModalPrice,
    FiltersModalSearchableList,
    FiltersModalSimpleRange,
    FiltersModalSingleValue,
} from '@rocco/ui/filter-sort/components/filters-modal/elements';
import React from 'react';
import type { DisplayMode } from '@rocco/types/enum/display-mode';
import { FiltersModalLocation } from '@rocco/ui/filter-sort/components/filters-modal/elements/FiltersModalLocation';

export interface FilterModalElementProps<T, V> {
    filterConfigItem: T;
    filterState: V;
    onFilterStateUpdate: (state: V, shouldAggregate?: boolean) => void;
    displayMode: DisplayMode;
}

export const FiltersModalElement = (props: {
    filterConfigItem: FilterConfigModalItem;
    filterState: FilterState | null;
    onUpdateFilterState: (
        filterKey: FilterStatesKey,
        newFilterState: FilterState,
        shouldAggregate?: boolean,
    ) => void;
    onAlphabeticalGrouping: (args: AggregationArgs) => void;
    displayMode: DisplayMode;
}) => {
    const {
        filterConfigItem,
        filterState,
        onUpdateFilterState,
        onAlphabeticalGrouping,
        displayMode,
    } = props;

    if (filterState === null) {
        throw new UIConfigurationError(
            `Filter has a null state: ${filterConfigItem.filterKey}`,
        );
    }

    const handleUpdateState = (
        newState: FilterState,
        shouldAggregate?: boolean,
    ) => {
        onUpdateFilterState(
            filterConfigItem.filterKey,
            newState,
            shouldAggregate,
        );
    };

    if (isFilterConfigModalItemDefault(filterConfigItem)) {
        if (isSingleValueFilterState(filterState)) {
            return (
                <FiltersModalSingleValue
                    filterConfigItem={filterConfigItem}
                    filterState={filterState}
                    onFilterStateUpdate={handleUpdateState}
                    displayMode={displayMode}
                />
            );
        }

        if (isMultipleValuesFilterState(filterState)) {
            return (
                <FiltersModalMultipleValuesButtons
                    filterConfigItem={filterConfigItem}
                    filterState={filterState}
                    onFilterStateUpdate={handleUpdateState}
                    displayMode={displayMode}
                />
            );
        }

        if (isLocationFilterState(filterState)) {
            return (
                <FiltersModalLocation
                    filterConfigItem={filterConfigItem}
                    filterState={filterState}
                    onFilterStateUpdate={handleUpdateState}
                    displayMode={displayMode}
                />
            );
        }
    }

    if (
        isMultipleValuesFilterState(filterState) &&
        isFilterConfigModalItemMultiple(filterConfigItem)
    ) {
        return (
            <FiltersModalMultipleValues
                filterConfigItem={filterConfigItem}
                filterState={filterState}
                onFilterStateUpdate={handleUpdateState}
                displayMode={displayMode}
            />
        );
    }

    if (
        isMultipleValuesFilterState(filterState) &&
        isFilterConfigModalItemColor(filterConfigItem)
    ) {
        return (
            <FiltersModalColors
                filterConfigItem={filterConfigItem}
                filterState={filterState}
                onFilterStateUpdate={handleUpdateState}
                displayMode={displayMode}
            />
        );
    }

    if (
        isSearchableListFilterState(filterState) &&
        isFilterConfigModalItemSearchable(filterConfigItem)
    ) {
        return (
            <FiltersModalSearchableList
                filterConfigItem={filterConfigItem}
                filterState={filterState}
                onFilterStateUpdate={handleUpdateState}
                onAlphabeticalGrouping={onAlphabeticalGrouping}
                displayMode={displayMode}
            />
        );
    }

    if (
        isRangeFilterState(filterState) &&
        isFilterConfigModalItemPrice(filterConfigItem)
    ) {
        return (
            <FiltersModalPrice
                filterConfigItem={filterConfigItem}
                filterState={filterState}
                onFilterStateUpdate={handleUpdateState}
                displayMode={displayMode}
            />
        );
    }

    if (
        isRangeFilterState(filterState) &&
        isFilterConfigModalItemSimpleRange(filterConfigItem)
    ) {
        return (
            <FiltersModalSimpleRange
                filterConfigItem={filterConfigItem}
                filterState={filterState}
                onFilterStateUpdate={handleUpdateState}
                displayMode={displayMode}
            />
        );
    }

    return null;
};
